import { Injectable } from '@angular/core';
import {
  ApproveESVRInfoUpdate,
  BatchAddAdditionalESVRsCommand,
  BatchesClient,
  BatchExtraViewModel,
  BatchReconciliationViewModel,
  BatchReleaseESVRCommand,
  BatchStatus,
  BatchViewModel,
  DispatchESVRInfoUpdate,
  DisputeESVRCommand,
  EditESVRCommand,
  ESVRFillSiteInfoEditCommand,
  ESVRFillSiteSaveCommand,
  ESVRsClient,
  ESVRsDownloadQuery,
  ESVRSearchViewModel,
  ESVRSearchViewModelESVRTotalTypePagedResultResponse,
  ESVRSourceSiteInfoEditCommand,
  ESVRSourceSiteSaveCommand,
  ESVRsReqDownloadQuery,
  FileResponse,
  FillSitesClient,
  ForwardESVRsCommand,
  IBatchHistoryViewModel,
  NotifyESVRsReleasedCommand,
  PrepareESVRCommand,
  RejectESVRCommand,
  SourceSitesClient,
  UpdateBatchStatusCommand
} from '@services/cleanfill-api/cleanfill-api.generated';
import { ISearchQuery } from '@view-models/interfaces/search-query';
import { Observable, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class BatchDetailService {
  constructor(
    private batchesClient: BatchesClient,
    private ESVRClient: ESVRsClient,
    private sourceSitesClient: SourceSitesClient,
    private fillSitesClient: FillSitesClient
  ) { }

  getBatchSummary(batchId: string): Observable<BatchReconciliationViewModel> {
    return this.batchesClient.getBatchReconciliation(batchId, new Date().getTimezoneOffset().toString());
  }
  getBatchDetail(batchId: string): Observable<BatchViewModel> {
    return this.batchesClient.getBatchDetail(batchId, undefined);
  }
  getBatchExtraLoadsRequests(batchId: string): Observable<BatchExtraViewModel[]> {
    return this.batchesClient.getBatchExtrasByBatchId(batchId, undefined);
  }
  getBatchExtraLoadsDetails(batchId: string): Observable<BatchExtraViewModel> {
    return this.batchesClient.getBatchExtraDetail(batchId, undefined);
  }

  searchESVR(query: ISearchQuery): Observable<ESVRSearchViewModelESVRTotalTypePagedResultResponse> {
    return this.ESVRClient.searchESVR(query.Page, query.PageSize, query.SortBy, query.FilterBy, new Date().getTimezoneOffset().toString());
  }
  loadESVR(id: string): Observable<ESVRSearchViewModel> {
    return this.ESVRClient.getESVRDetail(id, new Date().getTimezoneOffset().toString());
  }

  toggleFavoriteESVRRequest(esvrId: string): Observable<void> {
    // TODO: BE needed
    // return this.ESVRClient.toggleFavoriteESVR(esvrId);
    return of<void>(null);
  }

  getBatchHistory(batchId: string): Observable<IBatchHistoryViewModel[]> {
    return this.batchesClient.getBatchHistoryStatus(batchId, new Date().getTimezoneOffset().toString());
  }

  exportToExcel(query: ISearchQuery): Observable<FileResponse> {
    // tslint:disable-next-line: max-line-length
    return this.batchesClient.eSVRReport(
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      new Date().getTimezoneOffset().toString()
    );
  }

  exportBatchLoadsToExcel(query: ISearchQuery): Observable<FileResponse> {
    // tslint:disable-next-line: max-line-length
    return this.batchesClient.batchLoadsReport(
      query.Page,
      query.PageSize,
      query.SortBy,
      query.FilterBy,
      new Date().getTimezoneOffset().toString()
    );
  }

  downloadESVRs(body: ESVRsDownloadQuery): Observable<FileResponse> {
    return this.batchesClient.downloadESVRs(new Date().getTimezoneOffset().toString(), body);
  }
  downloadTrucksBilling(body: ESVRsReqDownloadQuery): Observable<FileResponse> {
    return this.batchesClient.generateTrucksBillingPDF(new Date().getTimezoneOffset().toString(), body);
  }

  prepareESVRs(loads: PrepareESVRCommand): Observable<void> {
    return this.ESVRClient.prepareESVRs(new Date().getTimezoneOffset().toString(), loads);
  }

  forwardESVRs(loads: ForwardESVRsCommand): Observable<void> {
    return this.ESVRClient.forwardESVRs(new Date().getTimezoneOffset().toString(), loads);
  }

  dispatchESVR(uniqueid: string, body: DispatchESVRInfoUpdate): Observable<void> {
    return this.ESVRClient.dispatchESVR(uniqueid, new Date().getTimezoneOffset().toString(), body);
  }
  saveESVRSource(uniqueid: string, body: ESVRSourceSiteSaveCommand): Observable<void> {
    return this.ESVRClient.eSVRSourceSiteSave(uniqueid, new Date().getTimezoneOffset().toString(), body);
  }
  saveDispatchedESVRSource(sourceSiteUniqueId: string, uniqueid: string, body: ESVRSourceSiteInfoEditCommand): Observable<void> {
    return this.sourceSitesClient.eSVRSourceSiteInfoEdit(sourceSiteUniqueId, uniqueid, new Date().getTimezoneOffset().toString(), body);
  }
  saveESVRFill(uniqueid: string, body: ESVRFillSiteSaveCommand): Observable<void> {
    return this.ESVRClient.eSVRFillSiteSave(uniqueid, new Date().getTimezoneOffset().toString(), body);
  }
  saveRecievedESVRFill(fillSiteUniqueId: string, uniqueid: string, body: ESVRFillSiteInfoEditCommand): Observable<void> {
    return this.fillSitesClient.eSVRFillSiteInfoEdit(fillSiteUniqueId, uniqueid, new Date().getTimezoneOffset().toString(), body);
  }

  saveESVR(uniqueid: string, body: EditESVRCommand): Observable<void> {
    return this.ESVRClient.editESVR(uniqueid, new Date().getTimezoneOffset().toString(), body);
  }
  acceptESVR(uniqueid: string, body: ApproveESVRInfoUpdate): Observable<void> {
    return this.ESVRClient.approveESVR(uniqueid, new Date().getTimezoneOffset().toString(), body);
  }
  rejectESVR(uniqueid: string, body: RejectESVRCommand): Observable<void> {
    return this.ESVRClient.rejectESVR(uniqueid, new Date().getTimezoneOffset().toString(), body);
  }
  holdESVR(uniqueid: string, body: DisputeESVRCommand): Observable<void> {
    return this.ESVRClient.disputeESVR(uniqueid, new Date().getTimezoneOffset().toString(), body);
  }

  forwardPreparedESVR(esvrUniqueId: string): Observable<void> {
    return this.ESVRClient.forwardESVR(esvrUniqueId, new Date().getTimezoneOffset().toString());
  }

  releaseESVRs(batchId: string) {
    return this.batchesClient.releaseBatchESVR(
      batchId,
      new Date().getTimezoneOffset().toString(),
      new BatchReleaseESVRCommand({ uniqueId: batchId })
    );
  }

  notifyESVRsReleased(batchId: string, email: string) {
    return this.batchesClient.notifyESVRsReleased(
      batchId,
      new Date().getTimezoneOffset().toString(),
      new NotifyESVRsReleasedCommand({ emailBody: email })
    );
  }

  addAdditionalESVRs(batchId: string, estimatedLoads: number, estimatedWeight: number, estimatedVolume: number) {
    return this.batchesClient.addAdditionalESVRs(
      batchId,
      new Date().getTimezoneOffset().toString(),
      new BatchAddAdditionalESVRsCommand({ batchUniqueId: batchId, estimatedLoads, estimatedWeight, estimatedVolume })
    );
  }

  updateBatchStatus(batchId: string, status: BatchStatus) {
    return this.batchesClient.updateBatchStatus(
      batchId,
      new Date().getTimezoneOffset().toString(),
      new UpdateBatchStatusCommand({ uniqueId: batchId, status })
    );
  }
}
